// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, FileText, BookOpen, Calculator } from 'lucide-react';
import EspertoBustaPagaQuiz from '../components/EspertoBustaPagaQuiz';

const CEDOLINO_SECTIONS = [
  {
    id: 'testa',
    label: 'TESTA',
    labelColor: '#C9A84C',
    bg: '#2A2A28',
    border: '1px solid rgba(255,255,255,0.06)',
    // Deployment 16 — P0a: dati di esempio statici con [placeholder] in grigio corsivo
    note: 'Questi sono dati di esempio — la guida spiega come leggere una busta paga reale e non richiede i tuoi dati personali.',
    rows: [
      {
        id: 'azienda',
        display: 'Azienda: [Nome Azienda]  |  P.IVA: [12345678901]',
        title: 'Dati Azienda',
        explanation: 'Nome, indirizzo e P.IVA del datore di lavoro. Verifica che corrispondano al tuo contratto.',
      },
      {
        id: 'dipendente',
        display: 'Dipendente: [Nome Dipendente]  |  CF: [XXXXXXXXXXXXXXXXX]',
        title: 'Dati Dipendente',
        explanation: 'I tuoi dati anagrafici e codice fiscale. Controlla sempre che siano corretti — errori nel CF possono causare problemi fiscali.',
      },
      {
        id: 'periodo',
        display: 'Periodo: [Mese Anno]  |  Mensilità: [X/13]',
        title: 'Periodo di competenza',
        explanation: 'Il mese di competenza. I pagamenti avvengono solitamente il 27-28 del mese stesso. La 13ª mensilità arriva a dicembre.',
      },
      {
        id: 'livello',
        display: 'Livello: [Livello CCNL]  |  Assunto: [GG/MM/AAAA]',
        title: 'Livello contrattuale',
        explanation: 'Il tuo livello contrattuale determina lo stipendio minimo garantito dal CCNL del tuo settore.',
      },
    ],
  },
  {
    id: 'corpo',
    label: 'CORPO',
    labelColor: '#888780',
    bg: 'transparent',
    border: '1px solid rgba(255,255,255,0.06)',
    rows: [
      {
        id: 'retribuzione',
        display: 'Retribuzione base',
        value: '€2.083,33',
        title: 'Retribuzione base mensile',
        explanation: 'Lo stipendio fisso mensile per il tuo livello (RAL €25.000 diviso 12). È la voce principale della busta paga.',
      },
      {
        id: 'straordinari',
        display: 'Straordinari',
        value: '€180,00',
        title: 'Straordinari',
        explanation: 'Ore oltre l\'orario contrattuale. Dal 2026 tassate al 15% con aliquota agevolata per lavoro notturno e festivo.',
      },
      {
        id: 'indennita',
        display: 'Indennità varie',
        value: '€50,00',
        title: 'Indennità varie',
        explanation: 'Voci aggiuntive previste dal CCNL: turno, trasferta, buoni pasto. Non tutte contribuiscono al calcolo TFR.',
      },
      {
        id: 'competenze',
        display: 'Totale competenze lorde',
        value: '€2.313,33',
        title: 'Totale competenze lorde',
        explanation: 'Somma di tutte le voci positive: stipendio + straordinari + indennità. Base di calcolo per le trattenute.',
        highlight: true,
      },
      {
        id: 'irpef',
        display: 'IRPEF trattenuta',
        value: '−€436,50',
        negative: true,
        title: 'IRPEF trattenuta mensile',
        explanation: 'Imposta sul reddito calcolata sullo stipendio mensile (scaglioni 2026: 23%/33%/43%). Viene ricalcolata a conguaglio a dicembre.',
      },
      {
        id: 'inps',
        display: 'Contributi INPS dipendente',
        value: '−€212,69',
        negative: true,
        title: 'Contributi INPS a tuo carico',
        explanation: 'Il 9.19% della retribuzione lorda che versi all\'INPS per la pensione futura. Il datore ne versa altri ~23% che non appaiono in busta paga.',
      },
      {
        id: 'detrazioni',
        display: 'Detrazioni lavoro dipendente',
        value: '+€93,00',
        positive: true,
        title: 'Detrazioni automatiche',
        explanation: 'Riduzioni IRPEF automatiche per i lavoratori dipendenti. Se il reddito cambia potrebbero esserci aggiustamenti al conguaglio di dicembre.',
      },
    ],
  },
  {
    id: 'piede',
    label: 'PIEDE',
    labelColor: '#3D7A52',
    bg: 'rgba(61,122,82,0.04)',
    border: '1px solid rgba(201,168,76,0.2)',
    rows: [
      {
        id: 'netto',
        display: 'NETTO DA PAGARE',
        value: '€1.757,14',
        title: 'Netto da pagare',
        explanation: 'La cifra che arriva sul tuo conto corrente. Differenza tra competenze €2.313,33 e trattenute €649,19 di IRPEF netta e INPS. Nota: la tua azienda ti costa ~€3.050/mese — il cuneo fiscale italiano è tra i più alti d\'Europa.',
        hero: true,
      },
      {
        id: 'tfr',
        display: 'TFR accantonato questo mese',
        value: '€145,67',
        title: 'TFR mensile accantonato',
        explanation: 'Quota mensile di TFR maturata (retribuzione lorda diviso 13,5). Si accumula ogni mese. Dal luglio 2026 potrebbe andare automaticamente al fondo pensione per i neoassunti.',
      },
      {
        id: 'ferie',
        display: 'Ferie residue',
        value: '18 giorni',
        title: 'Ferie residue',
        explanation: 'Giorni maturati e non goduti. La legge garantisce minimo 4 settimane/anno (20 giorni lavorativi). In molti contratti le ferie non godute entro 18 mesi si perdono.',
      },
      {
        id: 'rol',
        display: 'Straordinari residui ROL',
        value: '12 ore',
        title: 'Banca ore / ROL',
        explanation: 'Ore maturate non ancora godute o pagate. Richiedile come riposo compensativo o liquidazione secondo il tuo CCNL.',
      },
    ],
  },
];

const AccordionRow = ({ row, sectionId, isOpen, onToggle }) => {
  const isHero = row.hero;
  return (
    <div
      className="cursor-pointer transition-all duration-200"
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.04)',
        borderRadius: isHero ? 8 : 0,
      }}
      data-testid={`busta-row-${row.id}`}
    >
      <div
        onClick={onToggle}
        className="flex items-center justify-between px-4 py-3 hover:bg-white/[0.04] transition-colors"
        style={{
          borderLeft: isOpen ? '3px solid #C9A84C' : '3px solid transparent',
          borderRadius: 4,
        }}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span
            className="text-sm font-medium truncate"
            style={{
              // Deployment 16 — P0a: TESTA placeholder in grigio corsivo #aaaaaa
              color: sectionId === 'testa' ? '#aaaaaa' : isHero ? '#C9A84C' : row.negative ? '#E07070' : row.positive ? '#3D7A52' : row.highlight ? '#E8E8E4' : '#888780',
              fontStyle: sectionId === 'testa' ? 'italic' : 'normal',
              fontSize: isHero ? 15 : 13,
              fontWeight: isHero ? 700 : row.highlight ? 600 : 400,
            }}
          >
            {row.display}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-2 shrink-0">
          {row.value && (
            <span
              style={{
                color: isHero ? '#C9A84C' : row.negative ? '#E07070' : row.positive ? '#3D7A52' : '#E8E8E4',
                fontSize: isHero ? 16 : 13,
                fontWeight: isHero ? 700 : row.highlight ? 600 : 400,
                minWidth: 80,
                textAlign: 'right',
              }}
            >
              {row.value}
            </span>
          )}
          <ChevronDown
            className="w-4 h-4 text-[#888780] transition-transform duration-300"
            style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div
              className="px-4 pb-3 pt-1"
              style={{ background: 'rgba(201,168,76,0.04)', borderLeft: '3px solid #C9A84C' }}
            >
              <p className="text-xs font-semibold text-[#C9A84C] mb-1">{row.title}</p>
              <p className="text-sm text-[#888780]" style={{ lineHeight: 1.6 }}>{row.explanation}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function GuidaBustaPaga() {
  const [openRow, setOpenRow] = useState(null);

  const handleToggle = (key) => {
    setOpenRow(prev => prev === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-[#1C1C1A]" data-testid="guida-busta-paga-page">
      <div className="container-wide py-10 max-w-3xl">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#C9A84C]" />
            <span className="text-xs text-[#888780] uppercase tracking-wider">Guida interattiva</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-3">
            Capire la tua busta paga
          </h1>
          <p className="text-[#888780]" style={{ fontSize: 15, lineHeight: 1.7 }}>
            Ogni voce spiegata in modo semplice. Clicca su una sezione per scoprire cosa significa.
          </p>
        </motion.div>

        {/* Example note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6 p-3 rounded-xl text-sm"
          style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#888780' }}
        >
          Esempio: RAL €30.000, dipendente settore privato, single, nessun figlio a carico
        </motion.div>

        {/* Cross-link: Lettore Busta Paga (calcolatore) */}
        <Link to="/calcolatori/lettore-busta-paga" data-testid="guida-link-lettore"
          className="block mb-6 p-4 rounded-xl transition-colors hover:bg-white/[0.04]"
          style={{ background: '#252523', border: '1px solid rgba(61,122,82,0.3)' }}>
          <div className="flex items-center gap-3">
            <Calculator className="w-5 h-5 text-[#3D7A52] flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-[#E8E8E4] text-sm">Vuoi calcolare il tuo netto in totale anonimato?</p>
              <p className="text-xs text-[#888780] mt-0.5">Apri il <span className="text-[#3D7A52]">Lettore Busta Paga</span> — calcolatore client-side, GDPR-compliant, nessun dato personale richiesto.</p>
            </div>
          </div>
        </Link>

        {/* Cedolino interattivo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-2xl overflow-hidden mb-6"
          style={{ border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="cedolino-interattivo"
        >
          {CEDOLINO_SECTIONS.map((section) => (
            <div key={section.id} style={{ background: section.bg, borderBottom: section.border }} data-testid={`cedolino-section-${section.id}`}>
              {/* Section label */}
              <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                <span
                  className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                  style={{ color: section.labelColor, background: `${section.labelColor}18`, border: `1px solid ${section.labelColor}40` }}
                >
                  {section.label}
                </span>
              </div>

              {/* Deployment 16 — P0a: informative note under TESTA title */}
              {section.note && (
                <div className="mx-4 my-2">
                  <div
                    className="sapiens-note-box text-xs"
                    style={{ background: 'rgba(212, 168, 67, 0.10)' }}
                    data-testid={`cedolino-note-${section.id}`}
                  >
                    {section.note}
                  </div>
                </div>
              )}

              {/* Rows */}
              <div>
                {section.rows.map((row) => {
                  const key = `${section.id}-${row.id}`;
                  return (
                    <AccordionRow
                      key={key}
                      row={row}
                      sectionId={section.id}
                      isOpen={openRow === key}
                      onToggle={() => handleToggle(key)}
                    />
                  );
                })}
              </div>
              <div className="h-2" />
            </div>
          ))}
        </motion.div>

        {/* Box pratico */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="p-5 rounded-2xl mb-8"
          style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}
          data-testid="box-pratico"
        >
          <p className="text-sm font-semibold text-[#C9A84C] mb-2">In pratica</p>
          <p className="text-sm text-[#888780]" style={{ lineHeight: 1.7 }}>
            Con RAL €30.000 guadagni <strong className="text-[#E8E8E4]">€2.500 lordi/mese</strong>. Ne ricevi <strong className="text-[#3D7A52]">€1.757 netti</strong>. Il tuo datore spende <strong className="text-[#E8E8E4]">~€3.000/mese</strong>. Il cuneo fiscale assorbe il <strong className="text-[#C9A84C]">41% del costo del lavoro</strong>.
          </p>
        </motion.div>

        {/* Approfondisci */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
          data-testid="approfondisci-section"
        >
          <h3 className="text-sm font-semibold text-[#888780] uppercase tracking-wider mb-4">Approfondisci</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              {
                label: 'Calcola il tuo stipendio netto esatto',
                sub: 'Dalla RAL al netto in un click',
                href: '/calcolatori?calc=stipendio',
                icon: <BookOpen className="w-4 h-4 text-[#3D7A52]" />,
              },
              {
                label: 'Cosa fare con il tuo TFR',
                sub: 'Azienda o fondo pensione?',
                href: '/calcolatori?calc=tfr',
                icon: <BookOpen className="w-4 h-4 text-[#3D7A52]" />,
              },
              {
                label: 'Glossario: TFR, IRPEF, Cuneo fiscale',
                sub: 'Tutti i termini spiegati',
                href: '/glossario',
                icon: <BookOpen className="w-4 h-4 text-[#C9A84C]" />,
              },
              {
                label: 'Lezione: Le basi del reddito',
                sub: 'Livello 1 — Base',
                href: '/percorsi',
                icon: <BookOpen className="w-4 h-4 text-[#C9A84C]" />,
              },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.href}
                className="flex items-center gap-3 p-4 rounded-xl transition-all hover:bg-white/[0.04]"
                style={{ border: '1px solid rgba(255,255,255,0.06)' }}
                data-testid={`approfondisci-link-${i}`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#E8E8E4] truncate">{item.label}</p>
                  <p className="text-xs text-[#888780]">{item.sub}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#888780] shrink-0" />
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Quiz Esperto di Busta Paga — Deployment 13 (embedded at bottom) */}
        <EspertoBustaPagaQuiz />
      </div>
    </div>
  );
}
